import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BankAccountCard = ({ account, onSync, onViewDetails, onReconnect }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'synced':
        return 'text-success bg-success/10 border-success/20';
      case 'connected':
        return 'text-success bg-success/10 border-success/20';
      case 'pending': case'connecting':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'error':
        return 'text-error bg-error/10 border-error/20';
      case 'not_connected':
        return 'text-warning bg-warning/10 border-warning/20';
      default:
        return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  const getStatusDisplay = (status, feedEnabled, feedStatus) => {
    if (!feedEnabled || feedStatus === 'inactive') {
      return { icon: '⚠️', text: 'Not Connected' };
    }
    
    switch (status) {
      case 'synced': case'connected':
        return { icon: '✓', text: 'Connected' };
      case 'pending': case'connecting':
        return { icon: '⏳', text: 'Connecting' };
      case 'error':
        return { icon: '⚠', text: 'Connection Error' };
      case 'not_connected':
        return { icon: '⚠️', text: 'Not Connected' };
      default:
        return { icon: '⏸', text: 'Inactive' };
    }
  };

  const getAccountTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'checking':
        return 'CreditCard';
      case 'savings':
        return 'PiggyBank';
      case 'credit card':
        return 'CreditCard';
      case 'digital wallet':
        return 'Wallet';
      default:
        return 'CreditCard';
    }
  };

  const getAccountTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'checking':
        return 'bg-blue-100 text-blue-600';
      case 'savings':
        return 'bg-green-100 text-green-600';
      case 'credit card':
        return 'bg-purple-100 text-purple-600';
      case 'digital wallet':
        return 'bg-orange-100 text-orange-600';
      default:
        return 'bg-primary/10 text-primary';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const getBalanceColor = (balance) => {
    if (balance > 0) return 'text-success';
    if (balance < 0) return 'text-error';
    return 'text-foreground';
  };

  const connectionStatus = getStatusDisplay(
    account?.connectionStatus || account?.syncStatus, 
    account?.feedEnabled, 
    account?.feedStatus
  );

  const isConnected = account?.feedEnabled && account?.feedStatus === 'active';
  const needsConnection = !account?.feedEnabled || account?.feedStatus === 'inactive';

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-all duration-200 group">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getAccountTypeColor(account?.accountType)}`}>
            <Icon name={getAccountTypeIcon(account?.accountType)} size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-base">{account?.bankName}</h3>
            <p className="text-sm text-muted-foreground">
              {account?.accountType} • ****{account?.accountNumber?.slice(-4)}
            </p>
            {account?.institution && (
              <p className="text-xs text-muted-foreground">{account?.institution}</p>
            )}
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(account?.connectionStatus || account?.syncStatus)}`}>
          {connectionStatus?.icon} {connectionStatus?.text}
        </div>
      </div>

      {/* Connection Warning */}
      {needsConnection && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-warning text-sm">⚠️</span>
              <p className="text-warning text-sm font-medium">Bank feed not connected</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onReconnect?.(account?.id)}
              className="text-warning hover:bg-warning hover:text-warning-foreground text-xs px-2 py-1"
            >
              Connect
            </Button>
          </div>
        </div>
      )}

      {/* Balances */}
      <div className="space-y-3 mb-4">
        <div>
          <p className="text-sm text-muted-foreground">Current Balance</p>
          <p className={`text-2xl font-bold ${getBalanceColor(account?.currentBalance)}`}>
            {formatCurrency(account?.currentBalance)}
          </p>
        </div>
        
        {account?.accountType === 'Credit Card' ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Available Credit</p>
              <p className="text-lg font-medium text-foreground">
                {formatCurrency(account?.availableBalance)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Credit Limit</p>
              <p className="text-sm font-medium text-muted-foreground">
                {formatCurrency(account?.creditLimit || 0)}
              </p>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-muted-foreground">Available Balance</p>
            <p className="text-lg font-medium text-foreground">
              {formatCurrency(account?.availableBalance)}
            </p>
          </div>
        )}
      </div>

      {/* Status Information */}
      <div className="flex items-center justify-between text-sm text-muted-foreground mb-4 p-3 bg-muted/20 rounded-lg">
        <div>
          <span className="font-medium">Last Sync:</span>
          <br />
          <span>{account?.lastSyncAt ? new Date(account?.lastSyncAt)?.toLocaleDateString() : 'Never'}</span>
        </div>
        <div className="text-right">
          <span className="font-medium">Status:</span>
          <br />
          <span className={`${needsConnection ? 'text-warning font-semibold' : 'text-success'}`}>
            {isConnected ? 'Active' : 'Needs Setup'}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center space-x-2">
        {needsConnection ? (
          <>
            <Button
              variant="default"
              size="sm"
              onClick={() => onReconnect?.(account?.id)}
              iconName="Link"
              iconPosition="left"
              className="flex-1"
            >
              Connect
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails?.(account?.id)}
              className="flex-1"
            >
              View Details
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSync?.(account?.id)}
              iconName="RefreshCw"
              iconPosition="left"
              disabled={account?.syncStatus === 'pending'}
              className="flex-1"
            >
              {account?.syncStatus === 'pending' ? 'Syncing...' : 'Sync'}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => onViewDetails?.(account?.id)}
              className="flex-1"
            >
              View Details
            </Button>
          </>
        )}
      </div>

      {/* Optional routing number for checking accounts */}
      {account?.routingNumber && (
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Routing: {account?.routingNumber}
          </p>
        </div>
      )}
    </div>
  );
};

export default BankAccountCard;